import React, {memo, useCallback, useMemo} from "react";
import Timeline, {
    DateHeader,
    TimelineHeaders,
    TimelineMarkers,
    TodayMarker,
    CustomMarker,
} from "react-calendar-timeline";
import * as moment from "moment-timezone";
import {Stack, Typography} from "@mui/material";
import {AutoSizer, List} from 'react-virtualized';

import '../css/App.css';
import formatDateHeader from '../helpers/FormatDateHeader';
import ShiftItemRenderer from "./availability/ShiftItemRenderer";
import TimeslotDetailedItemRenderer from "./availability/TimeslotDetailedItemRenderer";
import TimeslotBasicItemRenderer from "./availability/TimeslotBasicItemRenderer";
import {ScheduleViewTypes} from "../domain/ScheduleViewTypes";

const THIRTY_MINUTES_IN_MILLISECONDS = 30 * 60 * 1000;

function TimeslotAndSessionsTimeline({
    performerData,
    shiftData,
    currentViewType,
    bufferSize,
    onChildDetailsDrilledInto,
    onTimelineDoubleClick,
    onTimelineBoundsChanged,
    onTimelineTimeChanged,
    options,
    currentSortingDefinition
}) {
    const viewTimeStart = moment().add(-12, 'hour');
    const viewTimeEnd = moment().add(12, 'hour');
    const ROUNDING_MINUTES_FOR_SHIFT_START_TIME = 1000 * 60 * 30;
    const MAX_ZOOM_OUT_SCHEDULE_VIEW = moment.duration(6, 'months').asMilliseconds();

    const keys = useMemo(() => ({
        groupIdKey: 'id',
        groupTitleKey: 'displayName',
        itemIdKey: 'id',
        itemGroupKey: 'owner',
        itemTimeStartKey: 'from',
        itemTimeEndKey: 'to',
        itemTitleKey: 'title'
    }), []);

    const groupRenderer = useCallback(({ group }) => {
        let performerStyle = { color: 'black' };

        if (currentSortingDefinition?.stylingFunction) {
            performerStyle = currentSortingDefinition.stylingFunction(group);
        }

        return <Typography sx={{ ...performerStyle, display: 'flex', alignItems: 'center', height: '100%' }}>{group.displayName}</Typography>;
    }, [currentSortingDefinition]);

    const renderTimelineItem = useCallback((props, viewType) => {
        if (viewType === ScheduleViewTypes.SHIFT_ONLY) {
            return <ShiftItemRenderer {...props} />;
        } else if (viewType === ScheduleViewTypes.TIMESLOTS_ONLY) {
            return <TimeslotBasicItemRenderer {...props} />;
        } else {
            return (
                <TimeslotDetailedItemRenderer
                    {...props}
                    shiftData={shiftData}
                    onChildDetailsDrilledInto={onChildDetailsDrilledInto}
                    options={options}
                />
            );
        }
    }, [shiftData, currentViewType, onChildDetailsDrilledInto, options]);

    const renderVirtualizedList = useCallback(({ width, height }) => (
        <AutoSizer>
            {({ width, height }) => (
                <List
                    width={width}
                    height={height}
                    rowCount={shiftData.length}
                    rowHeight={100}
                    rowRenderer={({ index, style }) => (
                        <div style={style}>
                            {renderTimelineItem(shiftData[index], currentViewType)}
                        </div>
                    )}
                    overscanRowCount={5}  // add buffer
                />
            )}
        </AutoSizer>
    ), [shiftData, currentViewType, renderTimelineItem]);

    return (
        <Stack id='calendar'>
            <Timeline
                defaultTimeStart={viewTimeStart}
                defaultTimeEnd={viewTimeEnd}
                groups={performerData}
                items={shiftData}
                dragSnap={ROUNDING_MINUTES_FOR_SHIFT_START_TIME}
                keys={keys}
                buffer={bufferSize}
                itemTouchSendsClick={true}
                lineHeight={35}
                itemHeightRatio={0.95}
                minZoom={THIRTY_MINUTES_IN_MILLISECONDS}
                canMove={false}
                canResize={false}
                canChangeGroup={false}
                traditionalZoom={true}
                groupRenderer={groupRenderer}
                itemRenderer={(props) => renderTimelineItem(props, currentViewType)}
                onCanvasDoubleClick={onTimelineDoubleClick}
                onBoundsChange={onTimelineBoundsChanged}
                onTimeChange={onTimelineTimeChanged}
                maxZoom={MAX_ZOOM_OUT_SCHEDULE_VIEW}
            >
                {/* Custom formatter for date header in timeline view */}
                <TimelineHeaders style={{ position: "sticky", zIndex: 1000 }}>
                    <DateHeader unit='primaryHeader' />
                    <DateHeader labelFormat={formatDateHeader} />
                </TimelineHeaders>
                <TimelineMarkers>
                    <TodayMarker />
                    {/* A hack marker for VEGAS-239: timeline not appearing in center. DO NOT MODIFY. */}
                    <CustomMarker date={moment().add(2, 'days').toDate().getTime()}>
                        {({ styles, date }) => {
                            const customStyles = {
                                ...styles,
                                backgroundColor: 'transparent',
                                width: '1px'
                            }
                            return <div style={customStyles} />;
                        }}
                    </CustomMarker>
                </TimelineMarkers>
            </Timeline>
            {/* Render the virtualized list here */}
            {renderVirtualizedList}
        </Stack>
    );
}

export default memo(TimeslotAndSessionsTimeline);